import { useTranslation } from '@rhino-project/core/i18n';
import {
  useModelFiltersContext,
  useModelFiltersController,
  UseModelFiltersControllerOptions,
  useModelIndexContext
} from '@rhino-project/core/hooks';
import { useRenderPaths } from '../../hooks/renderPaths';
import { useGlobalComponentForModel } from '@rhino-project/core/hooks';
import { ModelFiltersProvider } from '@rhino-project/core/components/models';
import { ModelFilter } from './ModelFilter';
import {
  Button,
  Chip,
  CloseButton,
  DrawerBackdrop,
  DrawerBody,
  DrawerContent,
  DrawerDialog,
  DrawerFooter,
  DrawerHandle,
  DrawerHeader,
  DrawerHeading,
  Link,
  useMediaQuery
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { ReactNode, useCallback, useState } from 'react';

export const ModelFiltersPill = ({ path }: { path: string }) => {
  const { t } = useTranslation();
  const {
    methods: { setValue },
    pills,
    resetPill
  } = useModelFiltersContext();

  const handleClose = useCallback(() => {
    resetPill(path);
    // Default values are always null
    setValue(path, null);
  }, [path, resetPill, setValue]);

  return (
    <Chip className="shrink-0 cursor-pointer">
      {pills[path]}
      <CloseButton
        aria-label={t('rhino.frontend.messages.remove_filter')}
        onPress={handleClose}
      />
    </Chip>
  );
};

// Resets the form, the applied filters, the pills and the search in one shot;
// shared by the "Clear all" affordances in the bar and the panel footer.
export const useModelFiltersClearAll = () => {
  const {
    defaultValues,
    methods: { reset },
    setPills
  } = useModelFiltersContext();
  const { defaultState, setFilter, setSearch } = useModelIndexContext();

  return useCallback(() => {
    reset(defaultValues);
    setFilter({});
    setPills({});
    setSearch(defaultState?.search);
  }, [defaultValues, reset, setFilter, setPills, setSearch, defaultState]);
};

export const useModelFiltersActiveCount = () => {
  const { pills } = useModelFiltersContext();

  return Object.values(pills ?? {}).filter((value) => value != null).length;
};

export const ModelFiltersPills = ({ showClearAll = true }) => {
  const { t } = useTranslation();
  const { paths, pills } = useModelFiltersContext();
  const handleClearAll = useModelFiltersClearAll();
  const activeCount = useModelFiltersActiveCount();

  if (paths?.length <= 0) return null;

  return (
    <div className="flex min-w-0 flex-row flex-nowrap items-center gap-2 overflow-x-auto sm:flex-wrap">
      {Object.keys(pills).map(
        (p) => pills[p] != null && <ModelFiltersPill key={p} path={p} />
      )}
      {showClearAll && activeCount > 0 && (
        <Link
          className="shrink-0 cursor-pointer text-sm whitespace-nowrap"
          onPress={handleClearAll}
        >
          {t('rhino.frontend.messages.clear_all')}
        </Link>
      )}
    </div>
  );
};

export const ModelFiltersForm = () => {
  const { paths } = useModelFiltersContext();
  const renderPaths = useRenderPaths(paths, {
    Component: ModelFilter
  });

  return <>{renderPaths}</>;
};

export interface ModelFiltersTriggerProps {
  onPress: () => void;
}

export const ModelFiltersTrigger = ({ onPress }: ModelFiltersTriggerProps) => {
  const { t } = useTranslation();
  const activeCount = useModelFiltersActiveCount();

  return (
    <Button
      aria-label={t('rhino.frontend.messages.filters')}
      size="sm"
      variant="outline"
      className="shrink-0"
      onPress={onPress}
    >
      <Icon icon="bi:funnel" width="16" height="16" />
      {t('rhino.frontend.messages.filters')}
      {activeCount > 0 && (
        <Chip color="accent" size="sm">
          {activeCount}
        </Chip>
      )}
    </Button>
  );
};

export type ModelFiltersPlacement = 'bottom' | 'right' | 'left';

const DEFAULT_DESKTOP_BREAKPOINT = '(min-width: 640px)';

// UI knobs, settable per model/global through the rhino.config override
// system's `{ props: {...} }` merge, e.g.
// `components: { event: { ModelFilters: { props: { variant: 'inline' } } } }`.
export interface ModelFiltersUIOptions {
  // 'panel' is the compact bar + drawer; 'inline' is the always-visible
  // grid of filter inputs above the table; 'responsive' is inline at and
  // above the desktop breakpoint, panel below it
  variant?: 'panel' | 'inline' | 'responsive';
  // Force the drawer edge instead of the responsive bottom/right default
  placement?: ModelFiltersPlacement;
  title?: ReactNode;
  showClearAll?: boolean;
  // Media query above which the drawer docks to the side instead of
  // opening as a bottom sheet
  desktopBreakpoint?: string;
}

export interface ModelFiltersPanelProps extends Omit<
  ModelFiltersUIOptions,
  'variant'
> {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

// The filter form lives in a drawer: a bottom sheet on mobile, a side panel
// on larger screens. The bar itself stays a single row so the table below is
// not pushed out of the viewport.
export const ModelFiltersPanel = ({
  isOpen,
  onOpenChange,
  placement,
  title,
  showClearAll = true,
  desktopBreakpoint = DEFAULT_DESKTOP_BREAKPOINT
}: ModelFiltersPanelProps) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(desktopBreakpoint);
  const handleClearAll = useModelFiltersClearAll();

  const effectivePlacement = placement ?? (isDesktop ? 'right' : 'bottom');
  const isBottomSheet = effectivePlacement === 'bottom';

  return (
    <DrawerBackdrop isOpen={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent placement={effectivePlacement}>
        <DrawerDialog
          className={
            isBottomSheet ? 'max-h-[85dvh] w-full' : 'h-full w-96 max-w-[90vw]'
          }
        >
          {isBottomSheet && <DrawerHandle />}
          <DrawerHeader>
            <DrawerHeading>
              {title ?? t('rhino.frontend.messages.filters')}
            </DrawerHeading>
          </DrawerHeader>
          <DrawerBody>
            <div className="flex flex-col gap-4">
              <ModelFiltersForm />
            </div>
          </DrawerBody>
          <DrawerFooter>
            {showClearAll && (
              <Button variant="ghost" onPress={handleClearAll}>
                {t('rhino.frontend.messages.clear_all')}
              </Button>
            )}
            <Button variant="primary" onPress={() => onOpenChange(false)}>
              {t('rhino.frontend.messages.done')}
            </Button>
          </DrawerFooter>
        </DrawerDialog>
      </DrawerContent>
    </DrawerBackdrop>
  );
};

const ModelFiltersBar = ({
  variant = 'panel',
  showClearAll = true,
  desktopBreakpoint = DEFAULT_DESKTOP_BREAKPOINT,
  ...panelProps
}: ModelFiltersUIOptions) => {
  const { paths } = useModelFiltersContext();
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useMediaQuery(desktopBreakpoint);

  if (paths?.length <= 0) return null;

  const resolvedVariant =
    variant === 'responsive' ? (isDesktop ? 'inline' : 'panel') : variant;

  if (resolvedVariant === 'inline')
    return (
      <>
        <div className="my-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <ModelFiltersForm />
        </div>
        <div className="my-2">
          <ModelFiltersPills showClearAll={showClearAll} />
        </div>
      </>
    );

  return (
    <>
      <div className="my-2 flex min-w-0 flex-row items-center gap-2">
        <ModelFiltersTrigger onPress={() => setIsOpen(true)} />
        <ModelFiltersPills showClearAll={showClearAll} />
      </div>
      {/* The filter fields stay mounted while the panel is closed so that
          filters arriving from the url or the initial index state keep the
          applied filter and its pill in sync. */}
      {!isOpen && (
        <div className="hidden" aria-hidden>
          <ModelFiltersForm />
        </div>
      )}
      <ModelFiltersPanel
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        showClearAll={showClearAll}
        desktopBreakpoint={desktopBreakpoint}
        {...panelProps}
      />
    </>
  );
};

export type ModelFiltersProps = UseModelFiltersControllerOptions &
  ModelFiltersUIOptions;

export const ModelFiltersBase = ({
  variant,
  placement,
  title,
  showClearAll,
  desktopBreakpoint,
  ...props
}: ModelFiltersProps) => {
  const controller = useModelFiltersController(props);

  return (
    <ModelFiltersProvider {...controller}>
      <ModelFiltersBar
        variant={variant}
        placement={placement}
        title={title}
        showClearAll={showClearAll}
        desktopBreakpoint={desktopBreakpoint}
      />
    </ModelFiltersProvider>
  );
};

export const ModelFilters = (props: ModelFiltersProps) =>
  useGlobalComponentForModel('ModelFilters', ModelFiltersBase, props);
